import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { useEffect, useState } from "react"
import { FormProvider } from "../../contexts/CreateRecordFormContext"
import Feedback from "../../components/Feedback/Feedback"
import RecordModal from "../../components/RecordModal/RecordModal"
import CollapsibleGridCard from "../../components/CollapsibleGridCard"
import { Box, CircularProgress } from "@mui/material"
import { Construction, FileDownload } from "@mui/icons-material"
import OpportunitiesColumns from "../../lib/TableColumns/OpportunitiesColumns"
import { API } from "../../lib/api"
import { useAuth } from "../../contexts/AuthContext"
import { useGridApiRef } from "@mui/x-data-grid-pro"
import { GRID_SESSION_KEYS, readGridSessionState } from "../../lib/gridSessionState"

const Opportunities = () => {
    const [opportunities, setOpportunities] = useState([])
    const [selectedRows, setSelectedRows] = useState([]);
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""})
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const { user } = useAuth()
    const apiRef = useGridApiRef()
    const sessionUserId = user?.userId || localStorage.getItem("id")

    // -- Columns saved in lib/TableColumns for readability -- //
    const columns = OpportunitiesColumns({setLoading, setFeedback})

    // -- Keep track of selected rows -- //
    const handleSelectionChange = (selection) => setSelectedRows(selection)

    // -- State Management for Modal that updates a existing opportunity(s) -- //
    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseUpdateModal = () => setOpenUpdateModal(false);

    // -- Export current opportunities data from the grid view -- //
    const handleExport = () => {
        const today = new Date();
        const formattedDate = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

        apiRef.current.exportDataAsCsv({
            fileName: `Opportunities - ${formattedDate}`
        })
    }

    // -- API Request to Update opportunity(s) -- //
    const handleUpdateSubmit = async (opportunityData) => {
        const updatedFields = Object.entries(opportunityData).reduce((accumulator, [key, value]) => {
            if (value === undefined || value === null || value === "") {
                return accumulator
            }

            if (typeof value === "object" && !Array.isArray(value)) {
                if (Object.prototype.hasOwnProperty.call(value, "value")) {
                    accumulator[key] = value.value
                    return accumulator
                }

                if (Object.prototype.hasOwnProperty.call(value, "id")) {
                    accumulator[key] = value.id
                    return accumulator
                }

                if (Object.prototype.hasOwnProperty.call(value, "_id")) {
                    accumulator[key] = value._id
                    return accumulator
                }
            }

            accumulator[key] = value
            return accumulator
        }, {});

        const response = await API.Opportunities.Update(
            selectedRows.map(_id => ({
                _id,
                data: {
                    ...updatedFields,
                    lastUpdatedBy: user.email,
                    lastUpdatedDate: Date.now()
                }
            }))
        );

        if (response.status === 200) {
            const updatedOpportunities = response.data.data;
            const updatedById = new Map(updatedOpportunities.map(item => [String(item._id), item]));

            setOpportunities(prevOpps =>
                prevOpps.reduce((nextOpportunities, opportunity) => {
                    const nextOpportunity = updatedById.has(String(opportunity._id))
                        ? { ...opportunity, ...updatedById.get(String(opportunity._id)) }
                        : opportunity

                    const shouldIncludeOpportunity = showArchived
                        ? Boolean(nextOpportunity.archived)
                        : !Boolean(nextOpportunity.archived)

                    if (shouldIncludeOpportunity) {
                        nextOpportunities.push(nextOpportunity)
                    }

                    return nextOpportunities
                }, [])
            );
            setSelectedRows([])
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: "Error updating opportunities...", type: "error" }))
        }

        handleCloseUpdateModal()
    }

    // -- API Request to Delete opportunity(s) -- //
    const handleDeleteSubmit = async () => {
        const response = await API.Opportunities.Delete(selectedRows)

        if (response.status === 200) {
            setOpportunities(prevOpps => prevOpps.filter(opportunity => !selectedRows.includes(opportunity._id)));
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }

        setSelectedRows([])
        handleCloseUpdateModal()
    }

    // -- Toggle between active and archived opportunities -- //
    const handleSwitchDataViews = async (e) => {
        const shouldShowArchived = e.target.checked

        setShowArchived(shouldShowArchived)
        setLoading(true)

        const response = shouldShowArchived ? await API.Opportunities.FetchArchived() : await API.Opportunities.FetchAll()

        setOpportunities(response)
        setLoading(false)
    }

    // -- Send selected opportunities to dashboard -- //

    // -- Available fields when updating opportunities -- //
    const updateFields = [
        { name: 'owner', label: 'Owner', type: 'autocomplete', options: users },
        { name: 'salesStage', label: 'Stage', type: 'autocomplete', options: ['01 - Lead', '02 - Identified', '06 - Quote Sent', '08 - Won'] },
        { name: 'closeDate', label: "Close Date", type: 'date' },
        { name: 'archived', label: 'Archived', type: 'autocomplete', options: [{ value: false, label: 'false' }, { value: true, label: 'true' }] },
    ]

    // -- Define buttons for ActionButtons component -- //
    const actionButtons = [
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, icon: Construction, color: "primary" },
        { title: "Export Data", onClick: handleExport, icon: FileDownload, color: "primary" },
        { title: "Toggle Archived Opportunities", onClick: handleSwitchDataViews, toggle: true, showArchived: showArchived },
    ];

    useEffect(() => {
        async function getData() {
            try {
                const response = await API.Opportunities.FetchAll()
                const usersResponse = await API.Users.FetchAll()
                const formattedUsers = usersResponse.map(appUser => ({
                    value: appUser._id,
                    label: `${appUser.firstName} ${appUser.lastName}`
                }))

                const sessionGridState = readGridSessionState(GRID_SESSION_KEYS.opportunities, sessionUserId)

                setUsers(formattedUsers)
                setOpportunities(response)
                setFilterModel(sessionGridState?.filterModel || { items: [] })
            } catch (error) {
                setFeedback(prevData => ({ ...prevData, open: true, message: 'Error: Failed To Load Opportunities...', type: "error" }))
            } finally {
                setLoading(false)
            }
        }

        if (user?.userId) {
            getData()
        }
    }, [sessionUserId, user])

    return (
        <BaseWrapper content={
            <>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <FormProvider>
                        <Feedback props={feedback} setFeedback={setFeedback} />
                        <RecordModal open={openUpdateModal} handleClose={handleCloseUpdateModal} fields={updateFields} title="Update Opportunity" onSubmit={handleUpdateSubmit} onDelete={handleDeleteSubmit} isUpdate={true} />
                        <CollapsibleGridCard
                            title="Sales"
                            linkLabel="Opportunities"
                            rows={opportunities}
                            columns={columns}
                            getRowId={(row) => row._id}
                            checkboxSelection
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={handleSelectionChange}
                            actionButtons={actionButtons}
                            apiRef={apiRef}
                            sessionStorageKey={GRID_SESSION_KEYS.opportunities}
                            sessionUserId={sessionUserId}
                            initialPageSize={50}
                            pageSizeOptions={[50, 100, 250, 500]}
                            extraGridProps={{
                                onFilterModelChange: setFilterModel,
                                filterModel,
                            }}
                        />
                    </FormProvider>
                )}
            </>
        } />
    )
}

export default Opportunities
